import type {
  AbilityCategory,
  ModuleDefinition,
  SynergyAbilityModifier,
  SynergyDefinition,
  SynergyRequirement,
} from "./phase-three-types";

export interface SkillPoolCurationCategory {
  category: AbilityCategory;
  targetCount: number;
  tierTargets: [number, number, number, number, number];
  retainedIds: string[];
}

export interface SkillPoolCurationDocument {
  version: 1;
  categories: SkillPoolCurationCategory[];
}

const STRUCTURAL_TAGS = new Set([
  "attack",
  "area",
  "archaeology",
  "elite_attack",
  "enhancement",
  "growth",
  "healing",
  "labor",
  "expanded",
  "expanded_pool",
  "branch_variant",
  "branch_area",
  "branch_archaeology",
  "branch_attack",
  "branch_elite_attack",
  "branch_enhancement",
  "branch_growth",
  "branch_healing",
  "branch_labor",
  "domain",
  "strike",
  "legacy",
  "mythic",
  "elite_hunter",
]);

const THEME_NAMES_BY_TAG = new Map([
  ["fire", "炎阳"],
  ["frost", "霜月"],
  ["lightning", "雷云"],
  ["earth", "山岳"],
  ["shadow", "幽影"],
  ["holy", "圣辉"],
  ["nature", "森灵"],
  ["poison", "翠毒"],
  ["blood", "血月"],
  ["arcane", "奥术"],
  ["celestial", "群星"],
  ["abyss", "深渊"],
  ["beast", "荒兽"],
  ["construct", "机巧"],
  ["ocean", "潮汐"],
  ["sand", "流沙"],
  ["plague", "疫雾"],
  ["spirit", "先灵"],
  ["dragon", "古龙"],
]);

export function applySkillPoolCuration(
  modules: readonly ModuleDefinition[],
  document: SkillPoolCurationDocument,
): ModuleDefinition[] {
  const errors = validateSkillPoolCurationDocument(modules, document);
  if (errors.length > 0) {
    throw new Error(
      `技能池精简配置无效：\n${errors
        .map((message) => `- ${message}`)
        .join("\n")}`,
    );
  }

  const retainedByCategory = new Map(
    document.categories.map((entry) => [
      entry.category,
      new Set(entry.retainedIds),
    ]),
  );
  return modules.filter((module) => {
    const retainedIds = retainedByCategory.get(module.category);
    return !retainedIds || retainedIds.has(module.id);
  });
}

export function applySkillPoolCurationToSynergies(
  synergies: readonly SynergyDefinition[],
  sourceModules: readonly ModuleDefinition[],
  curatedModules: readonly ModuleDefinition[],
  document: SkillPoolCurationDocument,
): SynergyDefinition[] {
  const sourceById = new Map(sourceModules.map((module) => [module.id, module]));
  const curatedIds = new Set(curatedModules.map((module) => module.id));
  const curatedCategories = new Set(
    document.categories.map((entry) => entry.category),
  );
  const curatedByCategory = new Map<AbilityCategory, ModuleDefinition[]>();
  for (const module of curatedModules) {
    if (!curatedCategories.has(module.category)) {
      continue;
    }
    const candidates = curatedByCategory.get(module.category) ?? [];
    candidates.push(module);
    curatedByCategory.set(module.category, candidates);
  }

  return synergies.map((synergy) => {
    const replacements = new Map<string, string>();
    const usedRequirementIds = new Set<string>();
    const remapId = (moduleId: string, avoidUsed: boolean): string => {
      if (curatedIds.has(moduleId)) {
        return moduleId;
      }
      const cached = replacements.get(moduleId);
      if (cached) {
        return cached;
      }
      const source = sourceById.get(moduleId);
      if (!source || !curatedCategories.has(source.category)) {
        return moduleId;
      }
      const candidates = curatedByCategory.get(source.category) ?? [];
      const available =
        avoidUsed && candidates.some(
          (candidate) => !usedRequirementIds.has(candidate.id),
        )
          ? candidates.filter(
              (candidate) => !usedRequirementIds.has(candidate.id),
            )
          : candidates;
      const linkedReplacementId = getLinkedReplacementId(
        source,
        replacements,
        sourceById,
      );
      const rankedCandidates =
        linkedReplacementId &&
        available.some((candidate) => candidate.id === linkedReplacementId)
          ? available.filter(
              (candidate) => candidate.id === linkedReplacementId,
            )
          : available;
      const replacement = [...rankedCandidates].sort(
        (left, right) =>
          scoreReplacement(right, source) -
            scoreReplacement(left, source) ||
          left.id.localeCompare(right.id),
      )[0];
      if (!replacement) {
        throw new Error(`无法为已精简技能 ${moduleId} 找到羁绊替代项。`);
      }
      replacements.set(moduleId, replacement.id);
      return replacement.id;
    };

    const requirements: SynergyRequirement[] = synergy.requirements.map(
      (requirement) => {
        if (requirement.kind === "module") {
          const moduleId = remapId(requirement.moduleId, true);
          usedRequirementIds.add(moduleId);
          return { kind: "module", moduleId };
        }
        if (requirement.kind === "one_of") {
          const moduleIds = [
            ...new Set(
              requirement.moduleIds.map((moduleId) =>
                remapId(moduleId, true),
              ),
            ),
          ];
          for (const moduleId of moduleIds) {
            usedRequirementIds.add(moduleId);
          }
          return { kind: "one_of", moduleIds };
        }
        return requirement;
      },
    );

    const abilityModifiers = synergy.abilityModifiers?.map(
      (modifier): SynergyAbilityModifier => ({
        ...modifier,
        moduleIds: [
          ...new Set(
            modifier.moduleIds.map((moduleId) => remapId(moduleId, false)),
          ),
        ],
      }),
    );
    const nameReplacements = new Map<string, string>();
    const themeReplacements = new Map<string, string>();
    for (const [sourceId, replacementId] of replacements) {
      const source = sourceById.get(sourceId);
      const replacement = sourceById.get(replacementId);
      if (source && replacement && source.name !== replacement.name) {
        nameReplacements.set(source.name, replacement.name);
        const sourceTheme = getGeneratedThemeName(source);
        const replacementTheme = getGeneratedThemeName(replacement);
        if (
          sourceTheme &&
          replacementTheme &&
          sourceTheme !== replacementTheme
        ) {
          themeReplacements.set(sourceTheme, replacementTheme);
        }
      }
    }

    return {
      ...synergy,
      name: replaceSkillNames(
        replaceSkillNames(synergy.name, nameReplacements),
        themeReplacements,
      ),
      description: replaceSkillNames(
        replaceSkillNames(synergy.description, nameReplacements),
        themeReplacements,
      ),
      requirements,
      abilityModifiers,
    };
  });
}

export function validateSkillPoolCurationDocument(
  modules: readonly ModuleDefinition[],
  document: SkillPoolCurationDocument,
): string[] {
  const errors: string[] = [];
  if (document.version !== 1) {
    errors.push("技能池精简配置 version 必须为 1。");
  }
  if (!Array.isArray(document.categories) || document.categories.length === 0) {
    errors.push("技能池精简配置必须包含至少一个类型。");
    return errors;
  }

  const moduleById = new Map(modules.map((module) => [module.id, module]));
  const configuredCategories = new Set<AbilityCategory>();
  const retainedIds = new Set<string>();
  for (const entry of document.categories) {
    if (configuredCategories.has(entry.category)) {
      errors.push(`技能类型 ${entry.category} 重复配置。`);
    }
    configuredCategories.add(entry.category);
    if (
      !Number.isInteger(entry.targetCount) ||
      entry.targetCount <= 0 ||
      entry.targetCount !== entry.retainedIds.length
    ) {
      errors.push(
        `技能类型 ${entry.category} 的目标数量必须等于保留ID数量。`,
      );
    }
    if (
      !Array.isArray(entry.tierTargets) ||
      entry.tierTargets.length !== 5 ||
      entry.tierTargets.some(
        (count) => !Number.isInteger(count) || count < 0,
      ) ||
      entry.tierTargets.reduce((sum, count) => sum + count, 0) <= 0 ||
      entry.tierTargets
        .slice(0, 4)
        .reduce((sum, count) => sum + count, 0) +
        entry.tierTargets[4] * 2 !==
        entry.targetCount
    ) {
      errors.push(
        `技能类型 ${entry.category} 的目标必须是“一阶/二阶/三阶/四阶/五六阶升格链”五个非负整数，运行时数量应为前四档之和加末档的两倍。`,
      );
    }
    for (const id of entry.retainedIds) {
      if (retainedIds.has(id)) {
        errors.push(`保留技能ID重复：${id}。`);
      }
      retainedIds.add(id);
      const module = moduleById.get(id);
      if (!module) {
        errors.push(`待保留技能不存在：${id}。`);
      } else if (module.category !== entry.category) {
        errors.push(
          `待保留技能 ${id} 属于 ${module.category}，不是 ${entry.category}。`,
        );
      }
    }
    const runtimeTierCounts = [1, 2, 3, 4, 5, 6].map(
      (tier) =>
        entry.retainedIds.filter(
          (id) => moduleById.get(id)?.tier === tier,
        ).length,
    );
    const effectiveTierCounts = [
      ...runtimeTierCounts.slice(0, 4),
      runtimeTierCounts[4],
    ];
    if (
      entry.tierTargets?.length === 5 &&
      (runtimeTierCounts[4] !== runtimeTierCounts[5] ||
        effectiveTierCounts.some(
          (count, index) => count !== entry.tierTargets[index],
        ))
    ) {
      errors.push(
        `技能类型 ${entry.category} 的实际五档分布 ${effectiveTierCounts.join("/")}（运行时${runtimeTierCounts.join("/")}）与目标 ${entry.tierTargets.join("/")} 不一致。`,
      );
    }
  }

  const curatedIds = new Set(
    modules
      .filter(
        (module) =>
          !configuredCategories.has(module.category) ||
          retainedIds.has(module.id),
      )
      .map((module) => module.id),
  );
  for (const module of modules) {
    if (!curatedIds.has(module.id)) {
      continue;
    }
    for (const relatedId of [module.evolutionTo, module.evolutionFrom]) {
      if (relatedId && !curatedIds.has(relatedId)) {
        errors.push(`精简后升格链不完整：${module.id} -> ${relatedId}。`);
      }
    }
  }
  return [...new Set(errors)];
}

function getLinkedReplacementId(
  source: ModuleDefinition,
  replacements: ReadonlyMap<string, string>,
  sourceById: ReadonlyMap<string, ModuleDefinition>,
): string | undefined {
  if (source.evolutionFrom) {
    const retainedSourceId = replacements.get(source.evolutionFrom);
    return retainedSourceId
      ? sourceById.get(retainedSourceId)?.evolutionTo
      : undefined;
  }
  if (source.evolutionTo) {
    const retainedAscensionId = replacements.get(source.evolutionTo);
    return retainedAscensionId
      ? sourceById.get(retainedAscensionId)?.evolutionFrom
      : undefined;
  }
  return undefined;
}

function scoreReplacement(
  candidate: ModuleDefinition,
  source: ModuleDefinition,
): number {
  const sourceTags = getThemeTags(source);
  const candidateTags = new Set(getThemeTags(candidate));
  const sharedTags = sourceTags.filter((tag) => candidateTags.has(tag)).length;
  const sameTier = candidate.tier === source.tier ? 1 : 0;
  const sameTierWeight = source.tier >= 5 ? 12_000 : 2_000;
  const highestAvailableTierWeight =
    source.tier >= 5 ? candidate.tier * 12_000 : 0;
  const tierDistance = Math.abs(candidate.tier - source.tier);
  const sameTarget =
    candidate.combat?.target === source.combat?.target ? 1 : 0;
  const sameHidden = Boolean(candidate.hidden) === Boolean(source.hidden) ? 1 : 0;
  return (
    sharedTags * 10_000 +
    highestAvailableTierWeight +
    sameTier * sameTierWeight -
    tierDistance * 120 +
    sameTarget * 40 +
    sameHidden * 10
  );
}

function getGeneratedThemeName(module: ModuleDefinition): string | undefined {
  if (
    !module.id.startsWith("exp_") &&
    !module.id.startsWith("branch_exp_")
  ) {
    return undefined;
  }
  const tag = module.tags.find((candidate) =>
    THEME_NAMES_BY_TAG.has(candidate),
  );
  return tag ? THEME_NAMES_BY_TAG.get(tag) : undefined;
}

function getThemeTags(module: ModuleDefinition): string[] {
  return module.tags.filter(
    (tag) => !STRUCTURAL_TAGS.has(tag) && !tag.startsWith("branch_"),
  );
}

function replaceSkillNames(
  value: string,
  replacements: ReadonlyMap<string, string>,
): string {
  let result = value;
  for (const [source, target] of replacements) {
    result = result.split(source).join(target);
  }
  return result;
}
